import * as Sentry from "@sentry/react";
import debug from "debug";
import { computed, observable } from "mobx";
import { identity, pickBy } from "lodash";
import { BaseStore } from "../BaseStore";
import { CacheStore } from "../CacheStore/CacheStore";
import { SnackbarStore } from "../SnackbarStore";
import {
  GetMyProfile,
  MyProfileFragment,
  MyProfilePartialFragment,
  MyProfileUpdated,
  MyProfileUpdatedSubscription,
  UpdateFavoriteTeam
} from "@graphql";
import { UIStore } from "../UIStore";
import { MenuPathsEnum } from "@containers/MainMenu/items";
import { ConsultantFavouritesStore } from "../../consultants/consultant-favourites/consultant-favourites-store";
import { logSubscriptionError } from "@utils/log-subscription-error";

const log = debug("app:store:UserStore");

export class UserStore extends BaseStore {
  @observable myProfile: GetMyProfile.Return["me"] = null;
  myProfileQuery = GetMyProfile.funcWatch({});
  subscription: ReturnType<typeof MyProfileUpdated.func>;

  constructor(
    protected readonly snackbar: SnackbarStore,
    protected readonly cache: CacheStore,
    protected readonly ui: UIStore,
    protected readonly consultantFavourites: ConsultantFavouritesStore
  ) {
    super();
  }

  @computed get id() {
    return this.myProfile ? this.myProfile._id : null;
  }

  @computed get name() {
    return this.myProfile ? this.myProfile.Name : "";
  }

  @computed get canSms() {
    return Boolean(this.myProfile && this.myProfile.CellPhone);
  }

  init = () =>
    this.fetchProfile().then(() => {
      this.watchProfile();
      this.subscribeToProfileUpdates();
    });

  fetchProfile = () => {
    return GetMyProfile.func({}).then(({ data }) => {
      Sentry.setUser({
        id: data.me._id,
        username: data.me.Email,
        email: data.me.Email
      });

      this.updateProfile(data.me);
    });
  };

  refetchProfile = () => this.fetchProfile();

  watchProfile: any = () => {
    return this.myProfileQuery.subscribe({
      next: (value) => {
        if (value?.data?.me) {
          this.updateProfile(value.data.me);
        }
      },
      error: logSubscriptionError("UserStore.watchProfile")
    });
  };

  subscribeToProfileUpdates = () => {
    this.subscription = MyProfileUpdated.func({});

    this.subscription.subscribe({
      next: (args) => {
        const data: MyProfileUpdatedSubscription = args.data as any;

        if (!data) {
          return;
        }

        this.updateProfile(data.myProfileUpdated);
      },
      error: logSubscriptionError("UserStore.subscribeToProfileUpdates")
    });
  };

  updateProfile = (profile: MyProfileFragment | MyProfilePartialFragment) => {
    log("updateProfile", profile);
    const values = pickBy(profile, identity);
    this.myProfile = { ...this.myProfile, ...values };

    if (Array.isArray((this.myProfile as any)?.favouriteConsultantIds)) {
      this.consultantFavourites.setIds(
        (this.myProfile as any).favouriteConsultantIds as string[]
      );
    }

    if (this.myProfile?.todoBadges) {
      this.ui.setUserTicketTeams(
        this.myProfile.todoBadges.TeamBadges.map((x) => {
          return {
            teamRef: x.TeamRef,
            badge: x.TotalUnread
          };
        })
      );

      this.ui.setMainMenuBadges({
        menu: MenuPathsEnum.DASHBOARD,
        badge: this.myProfile.todoBadges.TotalUnread
      });
    }
  };

  updateFavoriteTeam = (teamName: string) => {
    log("updateFavoriteTeam", teamName);
    UpdateFavoriteTeam.func({ teamName });
  };

  hasPermission = (permission: keyof GetMyProfile.Return["me"]["Permissions"]) => {
    if (!this.myProfile) {
      return false;
    }

    return Boolean(this.myProfile.Permissions[permission]);
  };

  can = (action: string) => {
    return (
      this.myProfile.permissions.includes("*") ||
      this.myProfile.permissions.includes(action)
    );
  };

  inTeam = (team: string) => {
    return !!this.myProfile?.Teams.includes(team);
  };

  getSubordinates() {
    return this.myProfile.subordinates;
  }
}
